import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Users, DollarSign, Phone, Mail, Clock, Accessibility, Star, CheckCircle } from 'lucide-react';
import { BedListing } from '@/types/listings';
import { AssistedLivingScorecard } from '@/types/scorecard';
import { ScorecardService } from '@/services/ScorecardService';
import ScorecardDisplay from '@/components/scorecard/ScorecardDisplay';
import ReferralEmailModal from '@/components/referrals/ReferralEmailModal';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

interface ListingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  listing: BedListing;
}

const ListingDetailsModal: React.FC<ListingDetailsModalProps> = ({
  isOpen,
  onClose,
  listing,
}) => {
  const { user, profile } = useAuth();
  const [scorecard, setScorecard] = useState<AssistedLivingScorecard | null>(null);
  const [loadingScorecard, setLoadingScorecard] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  
  const isAuthenticated = !!user;
  const isProvider = profile?.role === 'provider';
  const isOwnListing = user && listing.created_by === user.id;
  const canViewContact = isAuthenticated && (!isProvider || isOwnListing);

  useEffect(() => {
    if (!isOpen) return;
    
    const fetchScorecard = async () => {
      setLoadingScorecard(true);
      try {
        const scorecardData = await ScorecardService.lookupScorecard({
          facilityName: listing.facility_name,
          county: listing.county
        });
        
        if (!scorecardData) {
          const scrapedData = await ScorecardService.scrapeScorecard(listing.facility_name);
          setScorecard(scrapedData);
        } else {
          setScorecard(scorecardData);
        }
      } catch (error) {
        console.error('Error fetching scorecard:', error);
      } finally {
        setLoadingScorecard(false);
      }
    };

    fetchScorecard();
  }, [isOpen, listing.facility_name, listing.county]);

  const availabilityColor = listing.beds_available > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{listing.facility_name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Header with key info */}
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex flex-wrap gap-2">
                <Badge className={availabilityColor}>
                  {listing.beds_available > 0 ? `${listing.beds_available} Available` : 'No Beds Available'}
                </Badge>
                <Badge variant="outline">{listing.care_level}</Badge>
                <Badge variant="outline">{listing.county} County</Badge>
                <Badge variant="outline">{listing.age_group}</Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                Updated {formatDistanceToNow(new Date(listing.last_updated))} ago
              </div>
            </div>

            {/* Location and Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <span>{listing.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5 text-muted-foreground" />
                  <span>${listing.rent_amount}/month</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <span>{listing.beds_available} of {listing.licensed_capacity} licensed beds available</span>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-semibold">Contact Information</h3>
                {canViewContact ? (
                  <>
                    <div className="flex items-center space-x-2">
                      <Phone className="h-5 w-5 text-muted-foreground" />
                      <span>{listing.contact_person} - {listing.contact_phone}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                      <span>{listing.contact_email}</span>
                    </div>
                  </>
                ) : (
                  <div className="bg-muted/50 border border-dashed rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      {!isAuthenticated 
                        ? 'Sign in to view contact information'
                        : 'Contact information not available to other providers'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {listing.description && (
              <div className="space-y-2">
                <h3 className="font-semibold">Description</h3>
                <p className="text-muted-foreground">{listing.description}</p>
              </div>
            )}

            {/* Services and Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold">Waiver Types Accepted</h3>
                <div className="flex flex-wrap gap-2">
                  {listing.waiver_types.map(waiver => (
                    <Badge key={waiver} variant="secondary">
                      {waiver}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Gender Accepted</h3>
                <div className="flex flex-wrap gap-2">
                  {listing.gender_accepted.map(gender => (
                    <Badge key={gender} variant="secondary">
                      {gender}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Accessibility Features */}
            {listing.accessibility && listing.accessibility.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Accessibility className="h-5 w-5" />
                  Accessibility Features
                </h3>
                <div className="flex flex-wrap gap-2">
                  {listing.accessibility.map(feature => (
                    <Badge key={feature} variant="outline" className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities */}
            {listing.amenities && listing.amenities.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold">Amenities</h3>
                <div className="flex flex-wrap gap-2">
                  {listing.amenities.map(amenity => (
                    <Badge key={amenity} variant="outline">
                      {amenity}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Minnesota Quality Scorecard */}
            {scorecard && (
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  Minnesota Quality Scorecard
                </h3>
                <ScorecardDisplay scorecard={scorecard} />
              </div>
            )}
            
            {loadingScorecard && (
              <div className="space-y-4">
                <h3 className="font-semibold">Minnesota Quality Scorecard</h3>
                <div className="text-sm text-muted-foreground">Loading quality ratings...</div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              {listing.beds_available > 0 && (
                <Button 
                  onClick={() => {
                    if (!isAuthenticated) {
                      onClose();
                      // Could redirect to login here
                      return;
                    }
                    setShowReferralModal(true);
                  }}
                >
                  Submit Referral
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ReferralEmailModal
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
        provider={{
          id: listing.id,
          facility_name: listing.facility_name,
          contact_email: listing.contact_email,
          contact_person: listing.contact_person,
        }}
      />
    </>
  );
};

export default ListingDetailsModal;